import { useCallback, useEffect, useMemo, useState } from "react";
import { ViewAndDownload } from "../Helper";
import Inline from "./Inline";
import Annotated from "./Annotated";
import { Block, Language, ListItem, NoteKind, ProductFaq } from "./types";

interface Props {
	faq: ProductFaq;
	lang: Language;
	downloadsTitle: string;
	noteLabels: Record<NoteKind, string>;
}

export default function FaqView({ faq, lang, downloadsTitle, noteLabels }: Props) {
	const [open, setOpen] = useState<Set<string>>(new Set());

	const titles = useMemo(() => {
		const map = new Map<string, string>();
		faq.categories.forEach((c) => c.questions.forEach((q) => map.set(q.id, q.title)));
		return map;
	}, [faq]);

	const titleOf = useCallback((id: string) => titles.get(id), [titles]);

	const openAndScroll = useCallback((id: string) => {
		setOpen((prev) => new Set(prev).add(id));
		// wait for the panel to render before measuring
		requestAnimationFrame(() => document.getElementById(`q-${id}`)?.scrollIntoView({ block: "start", behavior: "smooth" }));
	}, []);

	useEffect(() => {
		const id = window.location.hash.replace("#q-", "");
		if (id && titles.has(id)) openAndScroll(id);
	}, [titles, openAndScroll]);

	function toggle(id: string) {
		setOpen((prev) => {
			const next = new Set(prev);
			if (!next.delete(id)) next.add(id);
			return next;
		});
	}

	const inline = (text: string) => (
		<Inline text={text} titleOf={titleOf} onNavigate={openAndScroll} />
	);

	function renderItem(item: ListItem, i: number) {
		if (typeof item === "string") return <li key={i}>{inline(item)}</li>;
		return (
			<li key={i}>
				{inline(item.text)}
				<ul>
					{item.sub.map((s, j) => (
						<li key={j}>{inline(s)}</li>
					))}
				</ul>
			</li>
		);
	}

	function renderBlock(b: Block, i: number) {
		switch (b.t) {
			case "p":
				return <p key={i}>{inline(b.text)}</p>;
			case "h":
				return <h4 key={i} className="faq-sub">{b.text}</h4>;
			case "ol":
				return (
					<ol key={i} className="faq-steps" style={b.start ? { counterReset: `step ${b.start - 1}` } : undefined}>
						{b.items.map(renderItem)}
					</ol>
				);
			case "ul":
				return <ul key={i}>{b.items.map(renderItem)}</ul>;
			case "note":
				return (
					<p key={i} className={`faq-note faq-note--${b.kind}`}>
						<strong className="faq-note__label">{noteLabels[b.kind]}</strong>
						{inline(b.text)}
					</p>
				);
			case "img":
				return (
					<figure key={i} className="faq-figure">
						<img src={b.src} alt={b.alt} loading="lazy" />
						{b.caption && <figcaption>{b.caption}</figcaption>}
					</figure>
				);
			case "table":
				return (
					<div key={i} className="faq-table-wrap">
						<table className="eo-spec faq-table">
							<thead>
								<tr>{b.head.map((h, j) => <th key={j}>{h}</th>)}</tr>
							</thead>
							<tbody>
								{b.rows.map((r, j) => (
									<tr key={j}>{r.map((c, k) => <td key={k}>{c}</td>)}</tr>
								))}
							</tbody>
						</table>
						{b.caption && <p className="faq-table-caption">{b.caption}</p>}
					</div>
				);
			case "annotated":
				return <Annotated key={i} image={b.image} alt={b.alt} width={b.width} height={b.height} items={b.items} inline={inline} />;
		}
	}

	return (
		<div className="faq-layout">
			<nav className="faq-nav" aria-label={faq.title}>
				<ul>
					{faq.categories.map((c) => (
						<li key={c.id}>
							<a href={`#c-${c.id}`}>{c.title}</a>
						</li>
					))}
					{faq.downloads && (
						<li>
							<a href="#c-downloads">{downloadsTitle}</a>
						</li>
					)}
				</ul>
			</nav>

			<div className="faq-content">
				{faq.categories.map((c) => (
					<section key={c.id} id={`c-${c.id}`} className="faq-category">
						<h2 className="faq-category__title">{c.title}</h2>
						{c.questions.map((q) => {
							const isOpen = open.has(q.id);
							return (
								<div key={q.id} id={`q-${q.id}`} className={`faq-item${isOpen ? " is-open" : ""}`}>
									<h3 className="faq-item__heading">
										<button
											type="button"
											className="faq-item__button"
											aria-expanded={isOpen}
											aria-controls={`panel-${q.id}`}
											onClick={() => toggle(q.id)}
										>
											<span>{q.title}</span>
											<span className="faq-item__icon" aria-hidden="true" />
										</button>
									</h3>
									<div id={`panel-${q.id}`} className="faq-item__panel" hidden={!isOpen}>
										{q.blocks.map(renderBlock)}
									</div>
								</div>
							);
						})}
					</section>
				))}

				{faq.downloads && (
					<section id="c-downloads" className="faq-category faq-downloads">
						<h2 className="faq-category__title">{downloadsTitle}</h2>
						{faq.downloads.map((d, i) => (
							<div key={d.path}>
								{d.group && d.group !== faq.downloads?.[i - 1]?.group && <h4 className="faq-sub">{d.group}</h4>}
								<ViewAndDownload title={d.title} path={d.path} lang={lang} />
							</div>
						))}
					</section>
				)}
			</div>
		</div>
	);
}
